import { useSession } from 'next-auth/react';
import { useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { ApiResult, client } from '../api';
import { useProductsList } from '../products';
import { SUBSCRIPTIONS_URL } from '../subscriptions';
import {
  CUSTOMER_ENDPOINT,
  CUSTOMER_STATUS_ACTIVE,
  CUSTOMER_STATUS_LEAD,
  CUSTOMER_SMS_UPDATES_PARAM,
} from './constants';
import * as customerService from './service';

export { useAddress } from './useAddress';
export { formToModel, modelToForm } from './service';

const ANONYMOUS_USER = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
};

export const useSessionCustomer = (includeSMS = false) => {
  const { data: session, status } = useSession();
  const isSessionLoading = status === 'loading';
  const { isLoading: isCustomerLoading, ...customerApi } = useCustomer(
    session?.user?.id,
    includeSMS,
  );

  return {
    ...customerApi,
    isLoading: isSessionLoading || isCustomerLoading,
    isAuthenticated: status === 'authenticated',
  };
};

export function useCustomer(customerId, includeSMS = false) {
  const { mutate: globalMutate } = useSWRConfig();
  const is_sms = includeSMS ? CUSTOMER_SMS_UPDATES_PARAM : '';
  const { data, error, mutate } = useSWR(
    customerId ? `${CUSTOMER_ENDPOINT}${customerId}/${is_sms}` : null,
  );

  const updateCustomer = useCallback(
    async customer => {
      const response = await customerService.updateCustomer(data.id, customer);
      mutate(response.data, false);
      return response;
    },
    [data?.id, mutate],
  );

  const getCustomer = async customerId => {
    const response = await customerService.getCustomer(customerId);
    globalMutate(`${CUSTOMER_ENDPOINT}${customerId}/`, response.data, false);
    return response;
  };

  const updatePassword = useCallback(
    password =>
      ApiResult.callAsync(() => customerService.updatePassword(password)),
    [],
  );

  const createPassword = useCallback(
    password =>
      ApiResult.callAsync(() => customerService.createPassword(password)),
    [],
  );

  const forgotPassword = useCallback(
    (email, captchaToken) =>
      ApiResult.callAsync(() =>
        customerService.forgotPassword(email, captchaToken),
      ),
    [],
  );

  const validateNewPasswordRequestToken = useCallback(
    (customer_id, token) =>
      ApiResult.callAsync(() =>
        customerService.validateNewPasswordRequestToken(customer_id, token),
      ),
    [],
  );

  const recoverForgottenPassword = useCallback(
    (customerId, { new_password, confirm_password, token }) =>
      ApiResult.callAsync(() =>
        customerService.recoverForgottenPassword(customerId, {
          new_password,
          confirm_password,
          token,
        }),
      ),
    [],
  );

  const performCheckout = useCallback(
    () =>
      ApiResult.callAsync(async () => {
        const response = await customerService.performCheckout(customerId);
        mutate({ ...data, status: CUSTOMER_STATUS_ACTIVE }, true);
        globalMutate(SUBSCRIPTIONS_URL, null, true);
        return response;
      }),
    [customerId, data, mutate, globalMutate],
  );

  return {
    customer: customerId ? data : ANONYMOUS_USER,
    error,
    isLoading: customerId ? !data && !error : false,
    isLead: data?.status === CUSTOMER_STATUS_LEAD,
    isActive: data?.status === CUSTOMER_STATUS_ACTIVE,
    updateCustomer,
    getCustomer,
    updatePassword,
    createPassword,
    forgotPassword,
    validateNewPasswordRequestToken,
    recoverForgottenPassword,
    performCheckout,
  };
}

export const useCart = () => {
  const { data: session, status } = useSession();
  const isSessionLoading = status === 'loading';
  const userId = session?.user?.id;
  const { data, error, mutate, isValidating } = useSWR(
    userId ? `${CUSTOMER_ENDPOINT}${userId}/cart/` : null,
  );

  const addToCart = useCallback(
    ({ product_sku, subscription_sku, quantity, pet_id }) =>
      ApiResult.callAsync(async () => {
        const response = await client.put(
          `${CUSTOMER_ENDPOINT}${userId}/cart/`,
          {
            line_items: [
              {
                sku: product_sku,
                quantity,
                pet_id,
                subscription: {
                  sku: subscription_sku,
                },
              },
            ],
          },
        );
        mutate(response.data, false);
        return response;
      }),
    [userId, mutate],
  );

  const removeFromCart = useCallback(
    petId =>
      ApiResult.callAsync(async () => {
        const response = await client.delete(
          `${CUSTOMER_ENDPOINT}${userId}/cart/`,
          {
            data: {
              pet_ids: [petId],
            },
          },
        );
        mutate(response.data, false);
        return response;
      }),
    [mutate, userId],
  );

  const refreshCart = useCallback(
    ({ resyncTaxes = false } = {}) =>
      ApiResult.callAsync(async () => {
        if (!resyncTaxes) {
          return mutate();
        }

        const response = await client.get(
          `${CUSTOMER_ENDPOINT}${userId}/cart/?resync_taxes=true`,
        );
        return mutate(response.data, false);
      }),
    [userId, mutate],
  );

  const updateOfferCode = useCallback(
    offerCode =>
      ApiResult.callAsync(async () => {
        const response = await client.put(
          `${CUSTOMER_ENDPOINT}${userId}/cart/`,
          {
            coupon_code: offerCode,
          },
        );
        mutate(response.data, false);
        return response;
      }),
    [userId, mutate],
  );

  return {
    cart: data,
    isLoading: userId ? !error && !data : isSessionLoading,
    error,
    refreshCart,
    isValidating,
    addToCart,
    removeFromCart,
    updateOfferCode,
  };
};

export const useCartLineItemForPet = petId => {
  const cartApi = useCart();
  const lineItemForPet = cartApi.cart?.line_items.find(
    sub => sub.pet_id === petId,
  );

  return {
    ...cartApi,
    lineItemForPet,
  };
};

export const useCartProducts = () => {
  const cartApi = useCart();
  const productsListApi = useProductsList(
    cartApi.cart?.line_items.map(item => item.sku).sort(),
  );

  return {
    cartApi,
    productsListApi,
  };
};

export const useCartSubscriptionProducts = () => {
  const cartApi = useCart();
  const subscriptionProductsListApi = useProductsList(
    cartApi.cart?.line_items.map(item => item.subscription.sku).sort(),
  );

  return {
    cartApi,
    subscriptionProductsListApi,
  };
};
